import React, { useCallback, useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { Layout } from "@/components/Layout";
import api from "@/lib/api";
import { ArrowLeft, Printer, Share2, Copy, FileText, Eye, Stethoscope, Mail, Quote, Sparkles, FileDown } from "lucide-react";
import { format } from "date-fns";
import { toast } from "sonner";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogTrigger, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import AudioPlayer from "@/components/AudioPlayer";
import LanguagePicker from "@/components/LanguagePicker";
import EditableNarrative from "@/components/EditableNarrative";
import VersionsDrawer from "@/components/VersionsDrawer";
import { t, NATIVE_NAMES } from "@/lib/i18n";

const RTL_LANGS = new Set(["ar", "he", "fa", "ur"]);

export default function ReportView() {
  const { reportId } = useParams();
  const navigate = useNavigate();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sharing, setSharing] = useState(false);
  const [mode, setMode] = useState("patient");
  const [lang, setLang] = useState("en");
  const [currentNarrative, setCurrentNarrative] = useState("");
  const [currentNote, setCurrentNote] = useState("");
  const [currentDoctor, setCurrentDoctor] = useState("");

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/reports/${reportId}`);
      setReport(data);
      const en = data.translations?.en || data.patient_narrative || "";
      setCurrentNarrative(en);
      setCurrentDoctor(
        (data.doctor_translations || {}).en
        || data.doctor_narrative
        || ""
      );
      const sourceLang = data.clinician_note_lang || "en";
      const noteEn = (data.clinician_note_translations || {}).en
        || (sourceLang === "en" ? data.clinician_note : "")
        || "";
      setCurrentNote(noteEn);
      setLang("en");
    } catch {
      toast.error("Report not found");
      navigate("/dashboard");
    } finally {
      setLoading(false);
    }
    // Only real deps are `reportId` (the route param) and `navigate` (router hook).
    // Everything else referenced inside is either a module import (`api`, `toast`),
    // a local destructured variable (`data`, `en`, `sourceLang`, `noteEn`), or a
    // useState setter (always stable). Static analyzers can't tell these apart.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportId, navigate]);
  useEffect(() => { load(); }, [load]);

  // Auto-poll while report is still being generated. `id` below is the setInterval
  // return value (a local variable), NOT a missing dependency.
  useEffect(() => {
    if (!report || report.status === "ready" || report.status === "failed" || !report.status) return;
    const id = setInterval(load, 2500);
    return () => clearInterval(id);
  }, [report, load]);

  const toggleShare = async () => {
    setSharing(true);
    try {
      const { data } = await api.post(`/reports/${reportId}/share`, { enable: !report.share_token });
      setReport({ ...report, share_token: data.share_token });
      if (data.share_token) {
        const url = `${window.location.origin}/share/${data.share_token}`;
        await navigator.clipboard.writeText(url);
        toast.success("Share link copied to clipboard");
      } else {
        toast.success("Sharing disabled");
      }
    } catch {
      toast.error("Could not update share");
    } finally {
      setSharing(false);
    }
  };

  const copyShareLink = async () => {
    const url = `${window.location.origin}/share/${report.share_token}`;
    await navigator.clipboard.writeText(url);
    toast.success("Link copied");
  };

  const downloadPdf = async () => {
    const downloadMode = mode === "source" ? "patient" : mode;
    try {
      const res = await api.get(`/reports/${reportId}/pdf`, {
        params: { mode: downloadMode, lang },
        responseType: "blob",
      });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const dispo = res.headers?.["content-disposition"] || "";
      const match = /filename="?([^"]+)"?/i.exec(dispo);
      const filename = match?.[1] || `${report.patient_name}_${downloadMode}.pdf`;
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("PDF downloaded");
    } catch {
      toast.error("Couldn't generate PDF");
    }
  };

  if (loading) return <Layout><div className="spinner mx-auto mt-10" /></Layout>;
  if (!report) return null;

  if (report.status && report.status !== "ready" && report.status !== "failed") {
    return (
      <Layout>
        <div className="max-w-xl mx-auto py-16 text-center bg-white border rounded-lg p-10" style={{ borderColor: "var(--color-border)" }}>
          <div className="w-14 h-14 mx-auto rounded-full flex items-center justify-center mb-5" style={{ background: "var(--color-primary)" }}>
            <div className="spinner" style={{ borderColor: "rgba(255,255,255,0.4)", borderTopColor: "white" }} />
          </div>
          <h2 className="font-display text-2xl font-semibold">Still working on this report</h2>
          <p className="text-sm mt-2" style={{ color: "var(--color-text-secondary)" }}>
            {report.status === "extracting" ? "Reading the file…" : "Writing summaries…"}
          </p>
          <p className="text-xs mt-4" style={{ color: "var(--color-text-secondary)" }}>This page will refresh automatically.</p>
        </div>
      </Layout>
    );
  }

  if (report.status === "failed") {
    return (
      <Layout>
        <div className="max-w-xl mx-auto py-16 text-center bg-white border rounded-lg p-10" style={{ borderColor: "var(--color-border)" }}>
          <h2 className="font-display text-2xl font-semibold" style={{ color: "var(--color-error)" }}>Report generation failed</h2>
          <p className="text-sm mt-2" style={{ color: "var(--color-text-secondary)" }}>{report.error || "Unknown error"}</p>
          <button data-testid="report-retry-back" onClick={() => navigate("/upload")} className="mt-6 px-5 py-2.5 rounded-md text-white font-medium" style={{ background: "var(--color-primary)" }}>
            Try another upload
          </button>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="no-print">
        <Link data-testid="report-back-link" to={`/patients/${report.patient_id}`} className="inline-flex items-center gap-1 text-sm mb-6" style={{ color: "var(--color-text-secondary)" }}>
          <ArrowLeft className="w-3.5 h-3.5" /> Back to patient
        </Link>

        <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
          <div>
            <div className="text-xs uppercase tracking-widest" style={{ color: "var(--color-text-secondary)" }}>
              {report.modality} · {format(new Date(report.created_at), "d MMM yyyy")}
            </div>
            <h1 className="font-display text-3xl md:text-4xl font-semibold mt-2">{report.patient_name}</h1>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <SendEmailButton report={report} language={lang} />
            <button data-testid="report-download-pdf-button" onClick={downloadPdf} className="inline-flex items-center gap-2 px-4 py-2 rounded-md border font-medium hover:bg-white" style={{ borderColor: "var(--color-border)" }} title="Download a native PDF of this view">
              <FileDown className="w-4 h-4" /> Download PDF
            </button>
            <button data-testid="report-print-button" onClick={() => window.print()} className="inline-flex items-center gap-2 px-3 py-2 rounded-md border hover:bg-white" style={{ borderColor: "var(--color-border)" }} title="Print this page">
              <Printer className="w-4 h-4" />
            </button>
            <button data-testid="report-share-button" onClick={toggleShare} disabled={sharing} className="inline-flex items-center gap-2 px-4 py-2 rounded-md text-white font-medium disabled:opacity-60" style={{ background: "var(--color-primary)" }}>
              <Share2 className="w-4 h-4" /> {report.share_token ? "Sharing on" : "Get share link"}
            </button>
            {report.share_token && (
              <button data-testid="report-copy-share-button" onClick={copyShareLink} className="inline-flex items-center gap-2 px-3 py-2 rounded-md border" style={{ borderColor: "var(--color-border)" }} title="Copy share link">
                <Copy className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>

        <Tabs value={mode} onValueChange={setMode} className="mb-6">
          <TabsList className="bg-white border" style={{ borderColor: "var(--color-border)" }}>
            <TabsTrigger data-testid="report-tab-patient" value="patient" className="data-[state=active]:bg-[#2A4736] data-[state=active]:text-white px-5">
              <Eye className="w-4 h-4 mr-1.5" /> Patient view
            </TabsTrigger>
            <TabsTrigger data-testid="report-tab-doctor" value="doctor" className="data-[state=active]:bg-[#2A4736] data-[state=active]:text-white px-5">
              <Stethoscope className="w-4 h-4 mr-1.5" /> Doctor view
            </TabsTrigger>
            <TabsTrigger data-testid="report-tab-source" value="source" className="data-[state=active]:bg-[#2A4736] data-[state=active]:text-white px-5">
              <FileText className="w-4 h-4 mr-1.5" /> Original
            </TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      {mode === "patient" && (
        <article
          className="print-area max-w-3xl mx-auto bg-white p-8 md:p-16 border rounded-lg shadow-sm"
          style={{ borderColor: "var(--color-border)" }}
          dir={RTL_LANGS.has(lang) ? "rtl" : "ltr"}
        >
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="text-xs uppercase tracking-widest font-display" style={{ color: "var(--color-text-secondary)" }}>
                {t(lang, "forPatient", { name: report.patient_name })}
              </div>
              <h2 className="font-display text-3xl mt-3" style={{ color: "var(--color-text-primary)" }}>
                {t(lang, "yourReportTitle", { modality: report.modality })}
              </h2>
            </div>
            <VersionsDrawer reportId={report.report_id} editedAt={report.last_edited_at} onReverted={load} scope="patient" />
          </div>
          <div className="mt-6">
            <LanguagePicker
              value={lang}
              onChange={(code, { narrative, clinician_note, doctor_narrative }) => {
                setLang(code);
                setCurrentNarrative(narrative);
                setCurrentNote(clinician_note ?? "");
                if (doctor_narrative) setCurrentDoctor(doctor_narrative);
              }}
              source={{ type: "owner", id: report.report_id }}
              initialTranslations={report.translations || { en: report.patient_narrative }}
              initialNoteTranslations={report.clinician_note_translations || {}}
              initialDoctorTranslations={report.doctor_translations || { en: report.doctor_narrative }}
            />
            <AudioPlayer source={{ type: "owner", id: report.report_id }} language={lang} />
            <ClinicianNote
              note={currentNote || report.clinician_note}
              reportId={report.report_id}
              language={lang}
              sourceLang={report.clinician_note_lang || "en"}
              onSaved={load}
            />
            <EditableNarrative
              text={currentNarrative}
              serif
              testidPrefix="patient"
              onSave={async ({ text, edit_note }) => {
                try {
                  await api.post(`/reports/${report.report_id}/edit`, {
                    patient_narrative: text,
                    language: lang,
                    edit_note,
                  });
                  toast.success("Patient narrative updated");
                  await load();
                } catch (e) {
                  toast.error(e?.response?.data?.detail || "Save failed");
                }
              }}
            />
          </div>
          <div className="mt-12 pt-6 border-t text-xs" style={{ borderColor: "var(--color-border)", color: "var(--color-text-secondary)" }}>
            {t(lang, "disclaimer")}
          </div>
        </article>
      )}

      {mode === "doctor" && (
        <article className="print-area max-w-3xl mx-auto bg-white p-8 md:p-12 border rounded-lg shadow-sm" style={{ borderColor: "var(--color-border)" }}>
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="text-xs uppercase tracking-widest font-display" style={{ color: "var(--color-text-secondary)" }}>Clinical summary · {report.modality}</div>
              <h2 className="font-display text-2xl mt-3" style={{ color: "var(--color-text-primary)" }}>{report.patient_name}</h2>
            </div>
            <VersionsDrawer reportId={report.report_id} editedAt={report.last_edited_at} onReverted={load} scope="doctor" />
          </div>
          {report.metadata && (
            <div className="flex flex-wrap gap-x-6 gap-y-1 text-xs mt-2 mb-6" style={{ color: "var(--color-text-secondary)" }}>
              {report.metadata.age && <span><b className="font-medium" style={{ color: "var(--color-text-primary)" }}>Age:</b> {report.metadata.age}</span>}
              {report.metadata.sex && <span><b className="font-medium" style={{ color: "var(--color-text-primary)" }}>Sex:</b> {report.metadata.sex}</span>}
              {report.metadata.study_date && <span><b className="font-medium" style={{ color: "var(--color-text-primary)" }}>Study:</b> {report.metadata.study_date}</span>}
              {report.metadata.indication && <span><b className="font-medium" style={{ color: "var(--color-text-primary)" }}>Indication:</b> {report.metadata.indication}</span>}
            </div>
          )}
          <div className="mt-2">
            <LanguagePicker
              value={lang}
              onChange={(code, { narrative, clinician_note, doctor_narrative }) => {
                setLang(code);
                setCurrentNarrative(narrative);
                setCurrentNote(clinician_note ?? "");
                if (doctor_narrative) setCurrentDoctor(doctor_narrative);
              }}
              source={{ type: "owner", id: report.report_id }}
              initialTranslations={report.translations || { en: report.patient_narrative }}
              initialNoteTranslations={report.clinician_note_translations || {}}
              initialDoctorTranslations={report.doctor_translations || { en: report.doctor_narrative }}
            />
          </div>
          {lang !== "en" && (
            <div className="mb-4 flex items-center gap-2 text-xs px-3 py-2 rounded-md border" style={{ borderColor: "var(--color-border)", background: "var(--bg-canvas)", color: "var(--color-text-secondary)" }} data-testid="doctor-translated-badge">
              <Sparkles className="w-3.5 h-3.5" style={{ color: "var(--color-accent)" }} />
              <span>Translated by AI for multilingual handoff. Verify clinical terminology against the English source before sign-off.</span>
            </div>
          )}
          <EditableNarrative
            text={lang === "en" ? report.doctor_narrative : currentDoctor}
            testidPrefix="doctor"
            readOnly={lang !== "en"}
            onSave={async ({ text, edit_note }) => {
              try {
                await api.post(`/reports/${report.report_id}/edit`, {
                  doctor_narrative: text,
                  edit_note,
                });
                toast.success("Clinical summary updated");
                await load();
              } catch (e) {
                toast.error(e?.response?.data?.detail || "Save failed");
              }
            }}
          />
        </article>
      )}

      {mode === "source" && (
        <article className="print-area max-w-3xl mx-auto bg-white p-8 md:p-12 border rounded-lg shadow-sm" style={{ borderColor: "var(--color-border)" }}>
          <div className="text-xs uppercase tracking-widest font-display" style={{ color: "var(--color-text-secondary)" }}>Original extracted text</div>
          <h2 className="font-display text-2xl mt-3 mb-6">{report.original_filename}</h2>
          <pre className="whitespace-pre-wrap text-sm font-body leading-relaxed" style={{ color: "var(--color-text-primary)" }}>
            {report.extracted_text}
          </pre>
        </article>
      )}
    </Layout>
  );
}

export function NarrativeBlock({ text, serif }) {
  if (!text) return null;
  const blocks = text.split(/\n\n+/);
  return (
    <div className={serif ? "font-serif-report" : "font-body"}>
      {blocks.map((block, i) => {
        const lines = block.split("\n");
        const first = lines[0]?.trim();
        const isHeading = first && first.length < 60 && (first === first.toUpperCase() || /^(Hello|What we|What this|What happens|Goodbye|Take care)/i.test(first));
        const rest = lines.slice(isHeading ? 1 : 0).join("\n").trim();
        // Stable key: derive from the block contents + index (index disambiguates
        // duplicate paragraphs but content drives identity for React reconciliation).
        const blockKey = `${i}-${block.slice(0, 48)}`;
        return (
          <div key={blockKey} className="mb-6">
            {isHeading && (
              <h3 className={`${serif ? "font-display" : "font-display"} text-xl font-semibold mb-2`} style={{ color: "var(--color-primary)" }}>
                {first}
              </h3>
            )}
            {rest && (
              <p className={`${serif ? "text-[17px] leading-[1.75]" : "text-[15px] leading-[1.7]"} whitespace-pre-wrap`} style={{ color: "var(--color-text-primary)" }}>
                {rest}
              </p>
            )}
          </div>
        );
      })}
    </div>
  );
}


function SendEmailButton({ report, language }) {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState(report?.patient_email || "");
  const [sending, setSending] = useState(false);
  const [lastSentTo, setLastSentTo] = useState(null);

  // Re-prefill if the report loads/changes after dialog mount. `setEmail` is a
  // useState setter (stable) and `report` derives from props which is the only
  // semantic dep; full `report?.patient_email` + `email` express the intent.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (report?.patient_email && !email) setEmail(report.patient_email);
  }, [report?.patient_email, email]);

  const send = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const { data } = await api.post(`/reports/${report.report_id}/email`, {
        email,
        language,
        frontend_origin: window.location.origin,
      });
      setLastSentTo(email);
      toast.success("Report emailed to " + email);
      setOpen(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send email");
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button data-testid="report-email-button" className="inline-flex items-center gap-2 px-4 py-2 rounded-md border font-medium hover:bg-white" style={{ borderColor: "var(--color-border)" }}>
          <Mail className="w-4 h-4" /> Send to patient
        </button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Send to patient</DialogTitle>
        </DialogHeader>
        <form onSubmit={send} className="space-y-4">
          <p className="text-sm" style={{ color: "var(--color-text-secondary)" }}>
            We'll email the patient a private share link plus the {language?.toUpperCase()} MP3 audio attached.
          </p>
          <div>
            <label className="text-xs uppercase tracking-widest" style={{ color: "var(--color-text-secondary)" }}>Patient email</label>
            <input
              data-testid="email-input"
              type="email"
              required
              autoFocus
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="patient@example.com"
              className="mt-1.5 w-full border bg-white rounded-md px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-[#2A4736]"
              style={{ borderColor: "var(--color-border)" }}
            />
          </div>
          {lastSentTo && (
            <p className="text-xs" style={{ color: "var(--color-text-secondary)" }}>
              Last sent to <span className="font-medium" style={{ color: "var(--color-text-primary)" }}>{lastSentTo}</span>
            </p>
          )}
          <DialogFooter>
            <button data-testid="email-send-button" type="submit" disabled={sending} className="px-5 py-2.5 rounded-md text-white font-medium disabled:opacity-60" style={{ background: "var(--color-primary)" }}>
              {sending ? "Sending…" : "Send email"}
            </button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}



export function ClinicianNote({ note, reportId, language, sourceLang, onSaved, readOnly }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(note || "");
  const [saving, setSaving] = useState(false);
  const lang = language || "en";
  const source = sourceLang || "en";
  const isMachineTranslated = !!note && source !== lang;

  const save = async () => {
    setSaving(true);
    try {
      await api.post(`/reports/${reportId}/edit`, {
        clinician_note: draft.trim(),
        language: lang,
        edit_note: draft.trim() ? "added clinician note" : "removed clinician note",
      });
      toast.success("Note saved");
      setEditing(false);
      await onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  if (!note && readOnly) return null;

  if (!note && !editing && !readOnly) {
    return (
      <button
        data-testid="clinician-note-add-button"
        onClick={() => { setDraft(""); setEditing(true); }}
        className="no-print mb-6 inline-flex items-center gap-2 text-sm font-medium px-3 py-1.5 rounded-md border hover:bg-white"
        style={{ borderColor: "var(--color-border)", color: "var(--color-text-secondary)" }}
      >
        <Quote className="w-3.5 h-3.5" /> {t(lang, "addNoteButton")}
      </button>
    );
  }

  if (editing && !readOnly) {
    return (
      <div className="no-print mb-6 p-4 border rounded-lg" style={{ borderColor: "var(--color-border)", background: "var(--bg-canvas)" }}>
        <label className="text-xs uppercase tracking-widest" style={{ color: "var(--color-text-secondary)" }}>{t(lang, "clinicianNoteLabel")}</label>
        <textarea
          data-testid="clinician-note-textarea"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          rows={3}
          maxLength={600}
          placeholder={t(lang, "addNotePlaceholder")}
          className="mt-1.5 w-full border bg-white rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#2A4736]"
          style={{ borderColor: "var(--color-border)" }}
        />
        <div className="mt-3 flex items-center gap-2">
          <button data-testid="clinician-note-save" onClick={save} disabled={saving} className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-md text-white font-medium text-sm disabled:opacity-60" style={{ background: "var(--color-primary)" }}>
            {saving ? "Saving…" : t(lang, "saveNote")}
          </button>
          <button data-testid="clinician-note-cancel" onClick={() => { setEditing(false); setDraft(note || ""); }} disabled={saving} className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-md border font-medium text-sm" style={{ borderColor: "var(--color-border)" }}>
            {t(lang, "cancel")}
          </button>
        </div>
      </div>
    );
  }

  // Read-only display of the saved note
  return (
    <div className="mb-8 p-5 border-l-4 rounded-r-lg" style={{ borderColor: "var(--color-accent)", background: "rgba(199,124,99,0.06)" }}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          <Quote className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: "var(--color-accent)" }} />
          <div>
            <div className="text-xs uppercase tracking-widest font-display" style={{ color: "var(--color-text-secondary)" }}>{t(lang, "clinicianNoteLabel")}</div>
            <p className="font-serif-report text-[16px] leading-[1.65] mt-1.5" style={{ color: "var(--color-text-primary)" }}>{note}</p>
            {isMachineTranslated && (
              <div data-testid="clinician-note-ai-footnote" className="mt-3 inline-flex items-center gap-1.5 text-[11px]" style={{ color: "var(--color-text-secondary)" }}>
                <Sparkles className="w-3 h-3" style={{ color: "var(--color-accent)" }} />
                <span>{t(lang, "translatedByAi", { source: NATIVE_NAMES[source] || source.toUpperCase() })}</span>
              </div>
            )}
          </div>
        </div>
        {!readOnly && (
          <button
            data-testid="clinician-note-edit-button"
            onClick={() => { setDraft(note || ""); setEditing(true); }}
            className="no-print text-xs font-medium underline-offset-2 hover:underline flex-shrink-0"
            style={{ color: "var(--color-text-secondary)" }}
          >
            {t(lang, "edit")}
          </button>
        )}
      </div>
    </div>
  );
}
